import { TRPCClientError } from "@trpc/client";

/**
 * Raised by the tRPC fetch wrapper when the edge/platform returns an error with
 * a non-JSON body. The prime case is Vercel's plaintext "Too Many Requests"
 * (HTTP 429): tRPC would otherwise JSON.parse it and surface the opaque
 * "Unexpected token 'T'... is not valid JSON" error the user sees, with no way
 * for the query client to tell it was a transient rate limit worth backing off
 * on. Carrying the status (and any Retry-After hint) lets the client recover.
 */
export class EdgeHttpError extends Error {
  readonly status: number;
  readonly retryAfterMs: number | null;

  constructor(status: number, retryAfterMs: number | null = null) {
    super(
      status === 429
        ? "You're sending requests too quickly. Waiting a moment before retrying."
        : `The server returned an unexpected response (HTTP ${status}).`,
    );
    this.name = "EdgeHttpError";
    this.status = status;
    this.retryAfterMs = retryAfterMs;
    Object.setPrototypeOf(this, EdgeHttpError.prototype);
  }
}

/** Parse an HTTP `Retry-After` header (delta-seconds or HTTP-date) into ms. */
export function parseRetryAfterMs(headerValue: string | null): number | null {
  if (!headerValue) {
    return null;
  }
  const seconds = Number(headerValue);
  if (Number.isFinite(seconds)) {
    return Math.max(0, seconds * 1000);
  }
  const dateMs = Date.parse(headerValue);
  if (!Number.isNaN(dateMs)) {
    return Math.max(0, dateMs - Date.now());
  }
  return null;
}

/**
 * Best-effort HTTP status for an error thrown from a tRPC call. Walks the cause
 * chain so it works whether the error is a bare EdgeHttpError, a TRPCClientError
 * wrapping one (the fetch-wrapper path), or a tRPC-shaped error that already
 * carries `data.httpStatus` (our own rate-limit middleware).
 */
export function getHttpStatus(error: unknown): number | undefined {
  const seen = new Set<unknown>();
  let current: unknown = error;
  while (current && !seen.has(current)) {
    seen.add(current);
    if (current instanceof EdgeHttpError) {
      return current.status;
    }
    if (current instanceof TRPCClientError) {
      const httpStatus: unknown = current.data?.httpStatus;
      if (typeof httpStatus === "number") {
        return httpStatus;
      }
    }
    current = current instanceof Error ? current.cause : undefined;
  }
  return undefined;
}

/** Retry-After hint (ms) from an EdgeHttpError anywhere in the cause chain. */
export function getRetryAfterMs(error: unknown): number | null {
  const seen = new Set<unknown>();
  let current: unknown = error;
  while (current && !seen.has(current)) {
    seen.add(current);
    if (current instanceof EdgeHttpError) {
      return current.retryAfterMs;
    }
    current = current instanceof Error ? current.cause : undefined;
  }
  return null;
}

const MAX_RATE_LIMIT_RETRIES = 3;
const MAX_TRANSIENT_RETRIES = 2;
const MAX_RETRY_DELAY_MS = 20_000;

/**
 * Global query retry policy. A 429 is transient (a short per-client rate-limit
 * window), so back off and retry; other 4xx are deterministic and retrying them
 * only deepens a rate-limit storm, so give up immediately; 5xx and network
 * errors get a couple of retries.
 */
export function shouldRetryQuery(
  failureCount: number,
  error: unknown,
): boolean {
  const status = getHttpStatus(error);
  if (status === 429) {
    return failureCount < MAX_RATE_LIMIT_RETRIES;
  }
  if (status !== undefined && status >= 400 && status < 500) {
    return false;
  }
  return failureCount < MAX_TRANSIENT_RETRIES;
}

/**
 * Backoff with full jitter, honoring Retry-After when present. Jitter is the
 * point: without it, every in-flight query retries in lockstep and re-trips the
 * per-client limit in a synchronized wave. `random` is injectable for tests.
 */
export function getQueryRetryDelay(
  attemptIndex: number,
  error: unknown,
  random: () => number = Math.random,
): number {
  const retryAfter = getRetryAfterMs(error);
  if (retryAfter !== null) {
    return Math.min(retryAfter, MAX_RETRY_DELAY_MS) + random() * 500;
  }
  const base = Math.min(1000 * 2 ** attemptIndex, MAX_RETRY_DELAY_MS);
  return base / 2 + random() * (base / 2);
}
